/* ============================================================================
   <combo-tabs> — Web component that powers the Combo Tabs product section.
   - Switches primary tabs (Technology / Ingredients / Texture)
   - Switches Day/Night pill toggle inside applicable panels
   - Keyboard accessible (Arrow keys within tablists)
   ========================================================================== */
if (!customElements.get('combo-tabs')) {
  class ComboTabs extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'bundle';
      this.bindMainTabs();
      this.bindSideToggles();
      this.bindTooltipToggles();
      this.bindPackSync();
    }

    // ------- Sync Day/Night toggles with external Pack Picker (pack:changed) -------
    bindPackSync() {
      document.addEventListener('pack:changed', (e) => {
        const packKey = e.detail && e.detail.packKey;
        if (!packKey) return;
        // Map pack key -> side. am/day -> day, pm/night -> night, bundle -> keep or default day
        let side = null;
        if (packKey === 'am' || packKey === 'day') side = 'day';
        else if (packKey === 'pm' || packKey === 'night') side = 'night';
        if (!side) return;
        this.querySelectorAll('.combo-toggle').forEach((toggleGroup) => {
          const btn = toggleGroup.querySelector(`.combo-toggle__btn[data-side="${side}"]`);
          const buttons = toggleGroup.querySelectorAll('.combo-toggle__btn');
          if (btn && !btn.classList.contains('is-active')) {
            this.activateSide(btn, buttons, toggleGroup);
          }
        });
      });
    }

    // ------- Tooltip info-buttons (Ingredients callouts + items) -------
    bindTooltipToggles() {
      const infoButtons = this.querySelectorAll('.combo-ing-call__btn, .combo-ing-item__info');
      infoButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          // close any others
          this.querySelectorAll('[aria-expanded="true"]').forEach((b) => {
            if (b !== btn) b.setAttribute('aria-expanded', 'false');
          });
          btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        });
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.querySelectorAll('[aria-expanded="true"]').forEach((b) => {
            b.setAttribute('aria-expanded', 'false');
          });
        }
      });
    }

    // ------- Main tabs (top pill bar) -------
    bindMainTabs() {
      const tabs = this.querySelectorAll('.combo-tabs__tab');
      const panels = this.querySelectorAll('.combo-tabs__panel');
      if (!tabs.length) return;

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab, tabs, panels));
        tab.addEventListener('keydown', (e) => this.onTabKey(e, tabs, panels));
      });
    }

    activateTab(tab, tabs, panels) {
      const target = tab.dataset.tab;
      tabs.forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        t.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      panels.forEach((p) => {
        const isActive = p.dataset.panel === target;
        p.classList.toggle('is-active', isActive);
        if (isActive) {
          p.removeAttribute('hidden');
        } else {
          p.setAttribute('hidden', '');
        }
      });
    }

    onTabKey(e, tabs, panels) {
      const list = Array.from(tabs);
      const idx = list.indexOf(e.currentTarget);
      let next = null;
      if (e.key === 'ArrowRight') next = list[(idx + 1) % list.length];
      else if (e.key === 'ArrowLeft') next = list[(idx - 1 + list.length) % list.length];
      else if (e.key === 'Home') next = list[0];
      else if (e.key === 'End') next = list[list.length - 1];
      if (next) {
        e.preventDefault();
        this.activateTab(next, tabs, panels);
        next.focus();
      }
    }

    // ------- Day / Night pill toggles inside panels -------
    bindSideToggles() {
      const toggles = this.querySelectorAll('.combo-toggle');
      toggles.forEach((toggleGroup) => {
        const buttons = toggleGroup.querySelectorAll('.combo-toggle__btn');
        buttons.forEach((btn) => {
          btn.addEventListener('click', () => this.activateSide(btn, buttons, toggleGroup));
          btn.addEventListener('keydown', (e) => this.onSideKey(e, buttons, toggleGroup));
        });
      });
    }

    activateSide(btn, buttons, toggleGroup) {
      const side = btn.dataset.side;
      const panelName = btn.dataset.targetPanel;
      const panel = this.querySelector(`.combo-tabs__panel[data-panel="${panelName}"]`);
      if (!panel) return;

      buttons.forEach((b) => {
        const active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        b.setAttribute('tabindex', active ? '0' : '-1');
      });

      const sides = panel.querySelectorAll('[data-side-content]');
      sides.forEach((el) => {
        el.classList.toggle('is-active', el.dataset.sideContent === side);
      });
    }

    onSideKey(e, buttons, toggleGroup) {
      const list = Array.from(buttons);
      const idx = list.indexOf(e.currentTarget);
      let next = null;
      if (e.key === 'ArrowRight') next = list[(idx + 1) % list.length];
      else if (e.key === 'ArrowLeft') next = list[(idx - 1 + list.length) % list.length];
      if (next) {
        e.preventDefault();
        this.activateSide(next, buttons, next.closest('.combo-toggle'));
        next.focus();
      }
    }
  }

  customElements.define('combo-tabs', ComboTabs);
}
